import { useLayoutEffect, useEffect, useState } from "react";
import "../../assets/css/Driver.css";
import { FaTruck, FaChevronRight, FaSortAmountUpAlt, FaInfoCircle, FaTrashAlt, FaPlus, FaSearch, FaEuroSign, FaTimesCircle, FaCheck, FaTimes } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { transportsAPI, transportOrdersAPI } from '../../api/apiClient';
import PaginationComponent from '../common/PaginationComponent';
import { Spinner, Alert, Button, Modal, Badge, Form, InputGroup, Col, Row } from "react-bootstrap";
import Swal from 'sweetalert2';
import { formatNumber } from '../../utils/helpers';

const Transport = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("active");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [counts, setCounts] = useState({
    all: 0,
    car: 0,
    furniture: 0,
    completed: 0,
    orders: 0
  });
  
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  const [highlightedBookingId, setHighlightedBookingId] = useState(null);

  // Fetch transports based on tab and search
  const fetchTransports = async (page = 1, search = searchTerm, tab = activeTab) => {
    try {
      setLoading(true);
      setError('');
      
      let response;
      const params = {
        page,
        per_page: pagination.per_page,
        search,
      };

      switch (tab) {
        case "car":
          response = await transportsAPI.cars(params);
          break;
        case "furniture":
          response = await transportsAPI.furnitures(params);
          break;
        case "completed":
          response = await transportsAPI.completed(params);
          break;
        case "orders":
          // جلب طلبات السائقين (Transport Orders)
          response = await transportOrdersAPI.index(params);
          break;
        default:
          response = await transportsAPI.index(params);
      }

      if (response.success) {
        // معالجة البيانات حسب نوع الـ tab
        let fetchedData = [];
        let metaData = {};
        
        if (tab === "orders") {
          // بيانات transportOrdersAPI
          fetchedData = response.data.data || [];
          metaData = response.data.meta || {
            current_page: page,
            last_page: response.data.last_page || 1,
            per_page: response.data.per_page || 10,
            total: response.data.total || 0,
          };
          
          // تحديث العداد للـ orders
          if (response.data.counts) {
            setCounts(prev => ({
              ...prev,
              orders: response.data.counts.all || response.data.total || 0
            }));
          }
        } else {
          // بيانات transportsAPI
          fetchedData = response.data.data || response.data;
          metaData = response.data.meta || {
            current_page: page,
            last_page: response.data.last_page || 1,
            per_page: response.data.per_page || 10,
            total: response.data.total || 0,
          };
          
          if (response.data.counts) {
            setCounts(prev => ({
              ...prev,
              all: response.data.counts.all || 0,
              car: response.data.counts.car || 0,
              furniture: response.data.counts.furniture || 0,
              completed: response.data.counts.completed || 0,
            }));
          }
        }

        setData(fetchedData);
        setPagination(metaData);

        // Save current state in URL
        navigate(location.pathname, { 
          state: { 
            activeTab: tab,
            currentPage: page 
          } 
        });
      } else {
        setError(response.message || t('failed_to_fetch_transports'));
      }
    } catch (err) {
      setError(t('failed_to_fetch_transports'));
      console.error('Error fetching transports:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch counts for all tabs
  const fetchAllCounts = async () => {
    try {
      // جلب عدد transports
      const transportResponse = await transportsAPI.index({ per_page: 1 });
      if (transportResponse.success && transportResponse.data.counts) {
        setCounts(prev => ({
          ...prev,
          all: transportResponse.data.counts.all || 0,
          car: transportResponse.data.counts.car || 0,
          furniture: transportResponse.data.counts.furniture || 0,
          completed: transportResponse.data.counts.completed || 0,
        }));
      }
      
      // جلب عدد transport orders
      const orderResponse = await transportOrdersAPI.index({ per_page: 1 });
      if (orderResponse.success) {
        const orderCount = orderResponse.data.total || orderResponse.data.data?.length || 0;
        setCounts(prev => ({
          ...prev,
          orders: orderCount
        }));
      }
    } catch (err) {
      console.error('Error fetching counts:', err);
    }
  };

  const handleMarkPaid = async (id) => {
    Swal.fire({
      title: t("confirm_action"),
      text: t("confirm_mark_paid"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#6c757d",
      confirmButtonText: t("yes_mark_paid"),
      cancelButtonText: t("cancel"),
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setUpdating(true);
          const response = await transportsAPI.markPaid(id);
          
          if (response.success) {
            Swal.fire({
              icon: "success",
              title: t("success"),
              text: t("booking.marked_paid_successfully"),
              timer: 3000,
              showConfirmButton: false,
            });
            fetchTransports();
            fetchAllCounts();
          } else {
            throw new Error(response.message);
          }
        } catch (err) {
          Swal.fire({
            icon: "error",
            title: t("error"),
            text: err.message || t("failed_to_mark_paid"),
            timer: 3000,
          });
        } finally {
          setUpdating(false);
        }
      }
    });
  };

  // Debounced Search Effect
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm !== undefined) {
        fetchTransports(1, searchTerm, activeTab);
      }
    }, 1500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  useEffect(() => {
    if (activeTab) {
      fetchTransports(1, searchTerm, activeTab);
    }
  }, [activeTab]);

  useEffect(() => {
    // استعادة الـ state من الـ location إذا كان موجوداً
    const initialTab = location.state?.activeTab || "active";
    const initialPage = location.state?.currentPage || 1;
    const highlightId = location.state?.highlightId;
    
    setActiveTab(initialTab);
    setHighlightedBookingId(highlightId);
    
    fetchAllCounts();
    
    if (location.state) {
      navigate(location.pathname, { replace: true });
    }
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchTransports(1, searchTerm);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchTerm('');
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      const response = await transportsAPI.delete(deleteId);
      
      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: t('success'),
          text: t('booking.transport_deleted_successfully'),
          timer: 3000,
          showConfirmButton: false
        });
        fetchTransports(pagination.current_page);
        fetchAllCounts();
      } else {
        setError(response.message || t('failed_to_delete_transport'));
      }
    } catch (err) {
      setError(t('failed_to_delete_transport'));
      console.error('Error deleting transport:', err);
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
      setDeleteId(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const handleViewDetails = (id) => {
    const isOrderTab = activeTab === "orders";
    const path = isOrderTab 
      ? `/admin/transport-orders/details/${id}`
      : `/admin/transports/details/${id}`;
    
    navigate(path, {
      state: {
        fromPage: pagination.current_page,
        activeTab: activeTab,
        highlightId: id
      }
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getProgressPercentage = (item) => {
    if (!item) return 0;
    
    const statusOrder = ['pending', 'approved', 'assigned', 'received', 'completed'];
    const currentStatusIndex = statusOrder.indexOf(item.status);
    return Math.round((currentStatusIndex / (statusOrder.length - 1)) * 100);
  };

  const getProgressColor = (percentage) => {
    if (percentage < 40) return "#e74c3c";
    if (percentage < 80) return "#f39c12";
    return "#2ecc71";
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      cancelled: { variant: 'danger', text: t('cancelled') },
      rejected: { variant: 'danger', text: t('rejected') },
      approved: { variant: 'success', text: t('approved') },
      assigned: { variant: 'info', text: t('assigned') },
      received: { variant: 'secondary', text: t('received') },
      completed: { variant: 'success', text: t('completed') },
      pending: { variant: 'warning', text: t('pending') }
    };
    
    const config = statusConfig[status] || { variant: 'light', text: status };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  if (loading && data.length === 0) {
    return (
      <div className="driver-page">
        <div className="text-center py-4">
          <Spinner animation="border" />
          <p className="mt-2">{t('loading')}</p>
        </div>
      </div>
    );
  }

  const clearSearch = () => {
    setSearchTerm('');
    fetchTransports(1, '');
  };

  // دالة لعرض العنصر حسب نوع الـ tab
  const renderItem = (item, index) => {
    const isOrderTab = activeTab === "orders";
    const itemId = isOrderTab ? item.order_id : item.booking_id;
    const status = isOrderTab ? item.status : item.status;
    const user = isOrderTab ? item.driver_name : item.user?.user_name;
    const bookingFrom = isOrderTab ? item.transport?.booking_from : item.booking_from;
    const bookingTo = isOrderTab ? item.transport?.booking_to : item.booking_to;
    const distance = isOrderTab ? item.transport?.distance : item.distance;
    const totalWeight = isOrderTab ? item.transport?.total_weight : item.total_weight;
    const transportType = isOrderTab ? item.transport?.transport_type : item.transport_type;
    const itemType = isOrderTab ? item.transport?.item_type?.[0] : item.item_type?.[0];
    const numItems = isOrderTab ? item.transport?.num_items?.[0] : item.num_items?.[0];
    const dimensions = isOrderTab ? item.transport?.dimensions?.[0] : item.dimensions?.[0];
    const createdAt = isOrderTab ? item.created_at : item.created_at;

    return (
      <div 
        className={`order-card ${item.id === highlightedBookingId ? "booking-highlight" : ""}`} 
        key={item.id}
        id={`booking-${item.id}`}
      >
        <div className="order-header d-flex justify-content-between align-items-center flex-wrap">
          {!isOrderTab && (
          <span className="order-id">
            <strong>
              {t("Auftrag Nr.")}:
              {searchTerm ? (
                <span 
                  dangerouslySetInnerHTML={{
                    __html: itemId?.replace(
                      new RegExp(searchTerm, 'gi'), 
                      match => `<mark class="bg-warning">${match}</mark>`
                    )
                  }} 
                />
              ) : (
                itemId
              )}
            </strong>
          </span>
          )}

          {isOrderTab && (
          <Col md={4}>
            <div className="d-flex align-items-center">
              <FaTruck className="text-primary me-2" />
              <div>
                <h6 className="mb-0 fw-bold">
                  {t('order_id')}: 
                  {searchTerm ? (
                    <span 
                      dangerouslySetInnerHTML={{
                        __html: item.order_id?.replace(
                          new RegExp(searchTerm, 'gi'), 
                          match => `<mark class="bg-warning">${match}</mark>`
                        )
                      }} 
                    />
                  ) : (
                    item.order_id
                  )}
                </h6>
                <small className="text-muted">
                  {t('booking_id')}: 
                  <Button
                    variant="link"
                    className="p-0 text-decoration-none"
                    onClick={() => navigate(`/admin/transports/details/${item.transport?.id}`)}
                  >
                    {item.transport?.booking_id}
                  </Button>
                </small>
              </div>
            </div>
          </Col>
          )}

          <span className="driver">
            <strong>{isOrderTab ? t("driver") : t("user")}:</strong> 
            {user || t('not_assigned')}
          </span>

          {!isOrderTab && (
            <span className="created">{item.created_at}</span> 
          )}

          <span className="price d-flex justify-content-end flex-wrap">
            {getStatusBadge(status)}

            {!isOrderTab && (
              <Badge bg={item.payment_status ? 'success' : 'warning'} className="ms-2">
                {t(item.payment) || t('unpaid')}
              </Badge>
            )}

            {isOrderTab && (
              <>
                <Badge bg="info" className="ms-2">
                  {t('order')}
                </Badge>
                {(isOrderTab || item.price) && (
                  <Badge bg="light" text="dark" className="ms-2">
                    {item.price} €
                  </Badge>
                )}
              </>
            )}

            {item.invoice_status === 'canceled' && (
              <Badge bg="danger" className="ms-2">
                {t('canceled')}
              </Badge>
            )}

            {!isOrderTab && (
              transportType == "car" ? (
                <Badge bg={'success'} className="ms-2">
                  {t('car')}
                </Badge>
              ) : (
                <Badge bg={'success'} className="ms-2">
                  {t('furniture')}
                </Badge>
              )
            )}
          </span>
        </div>

        <div className="order-body">
          <div className="left d-flex justify-content-between flex-wrap">
            <div className="d-flex flex-column">
              <strong>{t("booking.transport_type")}:</strong> 
              {t(transportType) || t('not_available')}
            </div>
            <div className="d-flex flex-column">
              <strong>{t("booking.item_type")}:</strong> 
              {itemType || t('not_available')}
            </div>
            <div className="d-flex flex-column">
              {numItems && numItems !== '' ? (
                <>
                  <strong>{t("booking.num_items")}:</strong> {numItems} {t('items')}
                </>
              ) : null}
            </div>

            <div className="delivery-path-vertical">
              <div className="circle-label-vertical">
                <span className="circle"></span>
                <span className="label">{bookingFrom}</span>
              </div>
              <div className="line-container">
                <div className="line-vertical"></div>
              </div>
              <div className="circle-label-vertical">
                <span className="circle"></span>
                <span className="label">{bookingTo}</span>
              </div>
            </div>
          </div>

          <div className="right d-flex justify-content-between">
              <div className="progress-bar-container mt-2">
                <div className="progress-header d-flex justify-content-between align-items-center mb-1">
                  <strong>{t("progress")}:</strong>
                  <span className="progress-label">{getProgressPercentage(item)}%</span>
                </div>
                
                <div className="progress-bar-outer">
                  <div
                    className="progress-bar-inner"
                    style={{
                      width: `${getProgressPercentage(item)}%`,
                      background: getProgressColor(getProgressPercentage(item)),
                    }}
                  ></div>
                </div>
              </div>
            
            {!isOrderTab && dimensions && (
              <span className="price">
                <strong>{t("booking.dimensions")}:</strong> {dimensions}
              </span>
            )}
            
          </div>
        </div>

        <div className="order-footer">
          <div>
            <strong>{t("booking.loading_date")}:</strong>{" "}
            {formatDate(item.loading_date || item.transport?.loading_date || item.created_at)}
          </div>
          <div>
            <strong>{t("distance")}:</strong> {distance} | 
            <strong> {t("duration")}:</strong> {item.total_duration || item.transport?.total_duration}
          </div>
          <div>
            <strong>{t("total_weight")}:</strong> {formatNumber(totalWeight)} Kg
          </div>

          <div className="action-buttons">
            {!isOrderTab && item.payment_status === 0 && (
              <Button 
                size="sm"
                variant="outline-success"
                onClick={() => handleMarkPaid(item.id)}
                disabled={updating || item.payment_status}
              >
                <FaEuroSign /> {t("booking.mark_paid")}
              </Button>
            )}
            
            <Button
              variant="outline-info"
              size="sm"
              onClick={() => handleViewDetails(item.id)}
              className="mx-2"
            >
              <FaInfoCircle /> {t("details")}
            </Button>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => handleDeleteClick(item.id)}
            >
              <FaTrashAlt /> {t("delete")}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="driver-page">
        <div className="top-bar">
          <div className="d-flex gap-2">
            {/* Search Bar */}
            <Row className="mb-3">
              <Col md={12}>
                <InputGroup>
                  <InputGroup.Text><FaSearch /></InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder={t('search_transport')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <Button 
                      variant="outline-secondary" 
                      onClick={clearSearch}
                      title={t("reset_search")}
                    >
                      ✕
                    </Button>
                  )}
                </InputGroup>
              </Col>
            </Row>
          </div>
          
          <div className="tabs">
            <button
              className={activeTab === "active" ? "active" : ""}
              onClick={() => handleTabChange("active")}
            >
              <span className="count-badge">{counts.all || 0}</span>
              {t("all_transports")}
            </button>
            <button
              className={activeTab === "car" ? "active" : ""}
              onClick={() => handleTabChange("car")}
            >
              <span className="count-badge">{counts.car}</span>
              {t("car_transports")}
            </button>
            <button
              className={activeTab === "furniture" ? "active" : ""}
              onClick={() => handleTabChange("furniture")}
            >
              <span className="count-badge">{counts.furniture}</span>
              {t("furniture_transports")}
            </button>
            <button
              className={activeTab === "completed" ? "active" : ""}
              onClick={() => handleTabChange("completed")}
            >
              <span className="count-badge">{counts.completed}</span>
              {t("completed_transports")}
            </button>
            <button
              className={activeTab === "orders" ? "active" : ""}
              onClick={() => handleTabChange("orders")}
            >
              <span className="count-badge">{counts.orders}</span>
              {t("transport_orders")}
            </button>
          </div>
          
        </div>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <div className="orders">
          {data.length === 0 ? (
            <div className="text-center py-5">
              <h4>{t('no_transports_found')}</h4>
            </div>
          ) : (
            data.map((item, index) => renderItem(item, index))
          )}
        </div>

        {pagination.total > 0 && (
          <PaginationComponent
            currentPage={pagination.current_page}
            lastPage={pagination.last_page}
            onPageChange={fetchTransports}
            disabled={loading}
          />
        )}

        {/* Delete Confirmation Modal */}
        <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered>
          <Modal.Header closeButton>
            <Modal.Title>{t('confirm_deletion')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {t('booking.confirm_delete_transport')}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleDeleteCancel}>
              {t('cancel')}
            </Button>
            <Button variant="danger" onClick={handleDeleteConfirm} disabled={loading}>
              {loading ? t('deleting') : t('delete')}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
};

export default Transport;